import React, { useState, useEffect } from 'react';
import { X, Share2, Copy, Check, QrCode, DollarSign, User, CreditCard } from 'lucide-react';
import QRCode from 'qrcode';

// ======= Funções utilitárias =======

// Calcula CRC16 CCITT
const calculateCRC16 = (data: string): string => {
  const polynomial = 0x1021;
  let crc = 0xFFFF;

  for (let i = 0; i < data.length; i++) {
    crc ^= (data.charCodeAt(i) << 8);

    for (let j = 0; j < 8; j++) {
      if (crc & 0x8000) {
        crc = (crc << 1) ^ polynomial;
      } else {
        crc <<= 1;
      }
      crc &= 0xFFFF;
    }
  }

  return crc.toString(16).toUpperCase().padStart(4, "0");
};

// Cria campo EMV
const createEMVField = (id: string, value: string): string => {
  const length = value.length.toString().padStart(2, "0");
  return `${id}${length}${value}`;
};

// Formata chave PIX
const formatPixKeyForEMV = (pixKey: string, pixKeyType: string): string => {
  switch (pixKeyType) {
    case 'cpf':
    case 'cnpj':
      return pixKey.replace(/\D/g, '');
    case 'phone':
      const cleanPhone = pixKey.replace(/\D/g, '');
      if (cleanPhone.startsWith('55') && cleanPhone.length === 13) {
        return `+${cleanPhone}`;
      } else if (cleanPhone.length === 11) {
        return `+55${cleanPhone}`;
      } else if (cleanPhone.length === 10) {
        return `+55${cleanPhone}`;
      }
      return `+55${cleanPhone}`;
    case 'email':
    default:
      return pixKey.toLowerCase().trim();
  }
};

// Gera payload PIX válido
const generatePixEMV = (
  pixKey: string,
  pixKeyType: string,
  holderName: string,
  amount: number,
  description: string
): string => {
  const formattedPixKey = formatPixKeyForEMV(pixKey, pixKeyType);
  const cleanHolderName = holderName
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .substring(0, 25);

  let payload = "";

  // 00 - Payload Format Indicator
  payload += createEMVField("00", "01");

  // 01 - Point of Initiation Method (12 = estático)
  payload += createEMVField("01", "12");

  // 26 - Merchant Account Information
  let merchantAccount = "";
  merchantAccount += createEMVField("00", "BR.GOV.BCB.PIX");
  merchantAccount += createEMVField("01", formattedPixKey);
  if (description) {
    merchantAccount += createEMVField("02", description.substring(0, 25));
  }
  payload += createEMVField("26", merchantAccount);

  // 52 - Merchant Category Code
  payload += createEMVField("52", "0000");

  // 53 - Transaction Currency (986 = BRL)
  payload += createEMVField("53", "986");

  // 54 - Transaction Amount
  if (amount > 0) {
    payload += createEMVField("54", amount.toFixed(2));
  }

  // 58 - Country Code
  payload += createEMVField("58", "BR");

  // 59 - Merchant Name
  payload += createEMVField("59", cleanHolderName);

  // 60 - Merchant City (sem acento, máx 15 chars)
  payload += createEMVField("60", "SAO PAULO");

  // 62 - Additional Data Field Template
  let additionalData = "";
  const txid = `PRESIDA${Date.now().toString().slice(-6)}`;
  additionalData += createEMVField("05", txid);
  payload += createEMVField("62", additionalData);

  // 63 - CRC16 placeholder
  payload += "6304";

  // Calcula e adiciona CRC16
  const crc16 = calculateCRC16(payload);
  const finalPayload = payload + crc16;

  return finalPayload;
};

// ======= Componente Modal =======

interface PixQRModalProps {
  isOpen: boolean;
  onClose: () => void;
  pixKey: string;
  pixKeyType: 'cpf' | 'cnpj' | 'email' | 'phone';
  pixHolderName: string;
  amount: number;
  groupName: string;
  pixPublicToken?: string;
}

const PixQRModal: React.FC<PixQRModalProps> = ({
  isOpen,
  onClose,
  pixKey,
  pixKeyType,
  pixHolderName,
  amount,
  groupName,
  pixPublicToken
}) => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pixPayload, setPixPayload] = useState<string>('');

  const formatPixKey = (key: string, type: string) => {
    switch (type) {
      case 'cpf':
        const cpfDigits = key.replace(/\D/g, '');
        return cpfDigits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      case 'cnpj':
        const cnpjDigits = key.replace(/\D/g, '');
        return cnpjDigits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      case 'phone':
        const phoneDigits = key.replace(/\D/g, '');
        if (phoneDigits.length === 11) {
          return phoneDigits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        } else if (phoneDigits.length === 10) {
          return phoneDigits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
        }
        return key;
      default:
        return key;
    }
  };

  const generateQRCode = async () => {
    setLoading(true);
    try {
      const pixPayload = generatePixEMV(
        pixKey,
        pixKeyType,
        pixHolderName,
        amount,
        `Mensalidade ${groupName}`
      );

      setPixPayload(pixPayload);

      const qrUrl = await QRCode.toDataURL(pixPayload, {
        width: 300,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' },
        errorCorrectionLevel: 'M'
      });

      setQrCodeUrl(qrUrl);
    } catch (error) {
      console.error('Erro ao gerar QR code:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && pixKey && pixHolderName && amount > 0) {
      generateQRCode();
    }
  }, [isOpen, pixKey, pixHolderName, amount, groupName]);

  const copyPixPayload = async () => {
    try {
      await navigator.clipboard.writeText(pixPayload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Erro ao copiar payload:', error);
    }
  };

  const copyPixInfo = async () => {
    const pixInfo = `*PIX para pagamento - ${groupName}*

*Valor:* ${amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
*Chave PIX:* ${formatPixKey(pixKey, pixKeyType)}
*Titular:* ${pixHolderName}
*Descrição:* Mensalidade ${groupName}`;

    try {
      await navigator.clipboard.writeText(pixInfo);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Erro ao copiar info PIX:', error);
    }
  };

  const shareViaWhatsApp = () => {
    if (pixPublicToken) {
      // Se tem token público, compartilhar o link da página pública
      const publicPageUrl = `${window.location.origin}/pix/${pixPublicToken}`;
      const pixInfo = `💰 *Mensalidade ${groupName}*

*Valor:* ${amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}

🔗 *Link de pagamento:*
${publicPageUrl}

_Clique no link acima para acessar a página de pagamento com QR Code PIX_

_Enviado via Presida.Club_`;

      const encodedMessage = encodeURIComponent(pixInfo);
      window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
    } else {
      // Fallback para o formato antigo se não tiver token público
      const pixInfo = `*PIX para pagamento - ${groupName}*

*Valor:* ${amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
*Chave PIX:* ${formatPixKey(pixKey, pixKeyType)}
*Titular:* ${pixHolderName}
*Descrição:* Mensalidade ${groupName}`;

      const encodedMessage = encodeURIComponent(pixInfo);
      window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-dark-paper rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b dark:border-dark-border">
          <h2 className="text-xl font-bold">QR Code PIX</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-dark-light rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* QR Code */}
          <div className="text-center mb-6">
            {loading ? (
              <div className="w-64 h-64 mx-auto bg-gray-100 dark:bg-dark-light rounded-lg flex items-center justify-center">
                <QrCode className="w-12 h-12 text-gray-400 animate-pulse" />
              </div>
            ) : qrCodeUrl ? (
              <div className="w-64 h-64 mx-auto bg-white p-4 rounded-lg border shadow-sm">
                <img src={qrCodeUrl} alt="QR Code PIX" className="w-full h-full object-contain" />
              </div>
            ) : (
              <div className="w-64 h-64 mx-auto bg-gray-100 dark:bg-dark-light rounded-lg flex items-center justify-center">
                <QrCode className="w-12 h-12 text-red-400" />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="space-y-4 mb-6">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-dark-light">
              <DollarSign className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm text-gray-500">Valor</p>
                <p className="font-bold text-green-600">
                  {amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-dark-light">
              <CreditCard className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm text-gray-500">Chave PIX ({pixKeyType.toUpperCase()})</p>
                <p className="font-medium">{formatPixKey(pixKey, pixKeyType)}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-dark-light">
              <User className="w-5 h-5 text-purple-600" />
              <div>
                <p className="text-sm text-gray-500">Titular</p>
                <p className="font-medium">{pixHolderName}</p>
              </div>
            </div>
          </div>

          {pixPayload && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-dark-light rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium">Payload EMV</h3>
                <button onClick={copyPixPayload} className="text-xs text-primary-600 hover:underline">
                  Copiar payload
                </button>
              </div>
              <p className="text-xs font-mono break-all text-gray-600">{pixPayload}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={copyPixInfo} className="btn-outline flex-1 flex items-center justify-center gap-2">
              {copied ? <><Check className="w-4 h-4" /> Copiado!</> : <><Copy className="w-4 h-4" /> Copiar Info</>}
            </button>

            <button onClick={shareViaWhatsApp} className="btn-primary flex-1 flex items-center justify-center gap-2">
              <Share2 className="w-4 h-4" /> Compartilhar
            </button>
          </div>

          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">Escaneie o QR Code ou use as informações acima para pagar via PIX</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PixQRModal;
